// A phase of a plan, as a fold (today's plans/PhaseCard.tsx): its head says
// its state, its numbers and its stages; its body its goal, its exit as a
// checklist, what it waits on and its issues by lane, each with its live
// state. Its tokens and the agents and models that worked each issue come
// from their sessions (Poulpe-761); today's time and gate are left out
// (Poulpe-410). A phase with work to dispatch has a Dispatch button at the
// head's end (dispatch.ts, Poulpe-581), beside the fold rather than in its
// summary, so that it is a button of its own and a click on it never folds
// the card.
import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { StateSlug } from '../api/types'
import { StateIcon } from '../components/Icons'
import { needsServer } from '../components/needsServer'
import { cx } from '../transcript/bits'
import { stateName } from '../vocab'
import { issuePath, PlanHtml } from './html'
import { canDispatch } from './dispatch'
import type { PlanPhase } from './model'
import { PhaseName, StageMeter } from './parts'
import { PHASE_ICON, type ConditionFacts, type Line, type PhaseFacts, type WaitFacts } from './state'
import { compact, day, planWords as w } from './words'
import { sumTokens, useIssueWork, type IssueWork } from './work'

export interface PhaseCardProps {
  facts: PhaseFacts
  /** Open at first: its default fold, or the address names it. */
  open: boolean
  /** Starts a dispatcher on the phase; none, and the card has no Dispatch button (an archived plan's). */
  onDispatch?: (phase: PlanPhase) => void
  /** The board is offline: the button is shown, and cannot be used. */
  offline?: boolean
}

export function PhaseCard({ facts, open: openAtFirst, onDispatch, offline = false }: PhaseCardProps) {
  const [open, setOpen] = useState(openAtFirst)
  const { phase, state } = facts
  const dispatch = onDispatch && canDispatch(facts)
  return (
    <div className={cx('pv-card-wrap', dispatch && 'has-dispatch')}>
    <details id={phase.id} className="pv-card" data-state={state} open={open} onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary className="pv-card-head">
        <span className="pv-chevron" aria-hidden="true" />
        <StateIcon state={PHASE_ICON[state]} size={16} />
        <h3>
          <PhaseName title={phase.title} />
        </h3>
        <span className="pv-pill">{w.phaseState[state]}</span>
        <Stats facts={facts} />
        {facts.total > 0 && <StageMeter counts={facts.counts} />}
      </summary>
      <div className="pv-card-body">
        {phase.goal && <PlanHtml as="p" className="pv-goal" content={phase.goal} mode="chip" />}
        {facts.conditions.length > 0 && <Exit conditions={facts.conditions} met={facts.met} />}
        {facts.waits.map((wait, index) => (
          <Wait key={index} facts={wait} />
        ))}
        {facts.lanes.some((lane) => lane.lines.length > 0) ? <IssueTable facts={facts} /> : <p className="pv-dim pv-empty">{w.noIssueYet}</p>}
      </div>
    </details>
    {dispatch && (
      <button
        type="button"
        className="button button-small pv-dispatch"
        aria-label={w.dispatchPhase(phase.title)}
        title={w.dispatchHint}
        disabled={offline}
        {...needsServer(offline)}
        onClick={() => onDispatch(phase)}
      >
        {w.dispatch}
      </button>
    )}
    </div>
  )
}

function Stats({ facts }: { facts: PhaseFacts }) {
  const work = useIssueWork()
  const { tokens } = sumTokens(work, facts.lanes.flatMap((lane) => lane.lines.flatMap((line) => (line.issue ? [line.issue.id] : []))))
  const bits: ReactNode[] = []
  if (facts.total > 0) {
    const [count, rest] = w.statLanded(facts.landed, facts.total)
    bits.push(
      <span key="landed">
        <strong>{count}</strong>
        {rest}
      </span>,
    )
  }
  if (tokens) bits.push(<span key="tokens" title={w.tokensTitle(tokens)}>{w.statTokens(tokens)}</span>)
  if (bits.length === 0) return null
  return <span className="pv-card-stats">{bits.flatMap((bit, i) => (i ? [' · ', bit] : [bit]))}</span>
}

function Exit({ conditions, met }: { conditions: readonly ConditionFacts[]; met: number }) {
  return (
    <div className="pv-exit">
      <span className="pv-label">{w.exitLabel(met, conditions.length)}</span>
      <ul>
        {conditions.map((c, index) => {
          const note =
            c.met === 'marked' ? w.metOn(day(c.condition.metOn)) : c.met === 'derived' ? w.issuesDone : c.named > 0 ? w.doneOf(c.done, c.named) : w.notMetNote
          return (
            <li key={index} data-met={c.met ? 'yes' : 'no'} data-how={c.met ?? undefined}>
              <StateIconLabelled state={c.met ? 'done' : 'todo'} label={c.met ? w.met : w.notMet} />
              <span>
                <PlanHtml content={c.condition.content} mode="icon" /> <span className="pv-note">{note}</span>
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/** The board's state icon, named for what it says here ("Met", "Not met") rather than for the state. */
function StateIconLabelled({ state, label }: { state: StateSlug; label: string }) {
  return (
    <span role="img" aria-label={label} className="pv-met-icon">
      <StateIcon state={state} size={14} />
    </span>
  )
}

function Wait({ facts }: { facts: WaitFacts }) {
  const { wait, stale } = facts
  return (
    <p className="pv-waits" data-on={wait.owner ? 'owner' : undefined}>
      <span className="pv-label">{wait.owner ? w.waitsOnYou : w.waitsOn}</span>
      <span className="pv-text">
        <PlanHtml content={wait.content} mode="chip" /> {stale && <span className="pv-flag">{w.stillWaiting}</span>}
      </span>
    </p>
  )
}

// ---------------------------------------------------------------- the issues

function IssueTable({ facts }: { facts: PhaseFacts }) {
  const work = useIssueWork()
  return (
    <div className="pv-table-wrap">
      <table className="pv-issues-table">
        <thead>
          <tr>
            <th scope="col" className="pv-col-issue">{w.colIssue}</th>
            <th scope="col" className="pv-col-state">{w.colState}</th>
            <th scope="col" className="pv-num pv-col-tokens">{w.colTokens}</th>
            <th scope="col" className="pv-col-agents">{w.colAgents}</th>
          </tr>
        </thead>
        <tbody>
          {facts.lanes.flatMap((lane, laneIndex) => [
            ...(lane.title
              ? [
                  <tr key={`lane-${laneIndex}`} className="pv-lane-row">
                    <th colSpan={4} scope="colgroup">
                      {lane.title}
                    </th>
                  </tr>,
                ]
              : []),
            ...lane.lines.map((line, index) => <IssueLine key={`${laneIndex}-${index}`} line={line} work={line.issue ? work.get(line.issue.id) : undefined} />),
          ])}
        </tbody>
      </table>
    </div>
  )
}

function IssueLine({ line, work }: { line: Line; work: IssueWork | undefined }) {
  const { item, issue } = line
  return (
    <tr>
      <td className="pv-col-issue">
        {issue ? (
          <Link className="sun-ref" to={issuePath(issue)}>
            {issue.key}
          </Link>
        ) : (
          <span className="pv-dim" title={w.unknownIssue}>
            {item.key}
          </span>
        )}{' '}
        <PlanHtml as="span" className="pv-title" content={item.title} mode="plain" />
        {item.note && <PlanHtml as="span" className="pv-note" content={item.note} mode="chip" />}
      </td>
      <td className="pv-col-state">
        {issue ? (
          <>
            <StateIcon state={issue.state} />
            <span>{stateName(issue.state)}</span>
          </>
        ) : (
          <span className="pv-dim" title={w.unknownIssue}>
            —
          </span>
        )}
      </td>
      <td className="pv-num pv-col-tokens">
        {work?.tokens != null ? <span title={w.tokensTitle(work.tokens)}>{compact(work.tokens)}</span> : <span className="pv-dim">{w.none}</span>}
      </td>
      <td className="pv-col-agents">
        {work && work.engines.length > 0 ? (
          work.engines.map(({ engine, models }) => {
            const name = w.engine[engine] ?? engine
            return (
              <span key={engine} className="pv-agent" data-engine={engine} title={w.agentTitle(name, models.map((m) => m.id))}>
                {models.length > 0 ? `${name} · ${models.map((m) => m.label).join(', ')}` : name}
              </span>
            )
          })
        ) : (
          <span className="pv-dim">{w.none}</span>
        )}
      </td>
    </tr>
  )
}
